import humps from "humps";
import DataTypes from "~data";
import { CustomerSuccessStage } from "~data/CustomerSuccessStage";
import { Feature } from "~data/Feature";
import { Url } from "~data/Url";
import { UtmParams } from "~data/UtmParams";

import { DirectLink } from "./components/ui/Link";

const { decamelize } = humps;

export const paths = {
  "hq.accounts.index": (p: { tag?: DataTypes["HqTags"]["id"] }) => `/accounts${query({ tags: p.tag })}`,
  "hq.accounts.show": (p: { companyId: string }) => `/accounts/${p.companyId}`,
  "hq.accounts.show.activityLog": (p: { companyId: string }) => `/accounts/${p.companyId}/activity_log`,
  "hq.accounts.show.charges": (p: { companyId: string; teamId: string }) =>
    `/accounts/${p.companyId}/charges/${p.teamId}`,
  "hq.accounts.show.companyDocuments": (p: { companyId: string }) => `/accounts/${p.companyId}/company_documents`,
  "hq.accounts.show.contacts": (p: { companyId: string; contactId?: string }) =>
    `/accounts/${p.companyId}/contacts${query({ contactId: p.contactId })}`,
  "hq.accounts.show.emails": (p: { companyId: string }) => `/accounts/${p.companyId}/emails`,
  "hq.accounts.show.members": (p: { companyId: string; teamId: string }) =>
    `/accounts/${p.companyId}/teams/${p.teamId}/members`,
  "hq.accounts.show.team": (p: { companyId: string; teamId: string }) => `/accounts/${p.companyId}/teams/${p.teamId}`,
  "hq.ai.activities": "/ai/activities",
  "hq.customerSuccess.index": "/cs",
  "hq.customerSuccess.list": (
    p: {
      stage: ":stage" | CustomerSuccessStage;
    }, // :stage for route generation
  ) => `/cs/${p.stage}`,
  "hq.debug.show": (p: { visitorId: string }) => `/debug/${p.visitorId}`,
  "hq.emailRedirect": "/emailredirect",
  "hq.globalActivityLog": (p: { userId?: DataTypes["Users"]["id"] }) => `/activity_log${query({ userId: p.userId })}`,
  "hq.quotes.index": "/quotes",
  "hq.quotes.show": (p: { quotePublicKey: string }) => `/quotes/${p.quotePublicKey}`,
  "hq.quotes.transfer.new": (p: { quotePublicKey: string }) => `/quotes/${p.quotePublicKey}/transfer`,
  "hq.referrals.index": "/referrals",
  "hq.root": "/",
  "hq.sales.activities": "/sales/activities",
  "hq.sales.index": "/sales",
  "hq.sales.leads": "/sales/leads",
  "hq.sales.opportunities.index": "/sales/opportunities",
  "hq.sales.opportunities.show": (p: { opportunityId: string }) => `/sales/opportunities/${p.opportunityId}`,
  "hq.sales.tasks": "/sales/tasks",
  "hq.search": (p: { q?: string; type?: string }) => `/search${query({ q: p.q, type: p.type })}`,
  "hq.users.show": (p: { userId: string }) => `/users/${p.userId}`,
  "hs.documentSharing.present": (p: { isEmbedded?: boolean; teamDomain: string }) =>
    `${p.isEmbedded ? "/embed" : ""}/present/${p.teamDomain}`,
  "hs.extension.uninstall": (p: { installationUuid?: string }) =>
    "/extension/uninstall" + query({ installationUuid: p.installationUuid }),
  "hs.root": (p: { utmParams?: UtmParams }) => `/${query({ ...p.utmParams })}`,
  "hs.templates.create": (p: { categoryId?: DataTypes["HelloscreenTemplateCategories"]["id"]; teamDomain: string }) =>
    `/library/${p.teamDomain}/new${query({ categoryId: p.categoryId })}`,
  "hs.templates.index": (p: { teamDomain: string }) => `/library/${p.teamDomain}`,
  "hs.templates.show": (p: { teamDomain: string; templateId: string }) => `/library/${p.teamDomain}/${p.templateId}`,
  "up.activity.activityLog": (p: { teamDomain: string; userId?: DataTypes["Users"]["id"] }) =>
    `/activity_log/${p.teamDomain}${query({ userId: p.userId })}`,
  "up.auth.invites.show": (p: { inviteToken: string }) => `/auth/invites/${p.inviteToken}`,
  "up.auth.join": (p: { teamDomain: string; teamUniqueIdentifier: string }) =>
    `/join/${p.teamUniqueIdentifier}-${p.teamDomain}`,
  "up.auth.login": (p: {
    email?: string;
    preauthorizedLoginToken?: string;
    redirectUrl?: string;
    returnHere?: boolean;
  }) =>
    "/auth/login" +
    query({
      email: p.email ?? null,
      redirectUrl: p.redirectUrl ?? (p.returnHere ? location.href : null),
      ...(p.preauthorizedLoginToken ? { preauthorizedLoginToken: p.preauthorizedLoginToken } : {}),
    }),
  "up.auth.mfa.enable": "/auth/mfa/setup",
  "up.auth.signUp": (p: { integration?: string; inviteToken?: string; utmParams?: UtmParams }) =>
    "/auth/sign_up" + query({ integration: p.integration, inviteToken: p.inviteToken, ...p.utmParams }),
  "up.integrations": (p: { teamDomain: string }) => `/settings/teams/${p.teamDomain}/integrations`,
  "up.integrations.google.connect": (p: { teamDomain: string }) =>
    `/settings/teams/${p.teamDomain}/integrations/add/google`,
  "up.invoice.showFailed": (p: { invoiceId: string }) => `/invoice/${p.invoiceId}/failed`,
  "up.members": (p: { teamDomain: string }) => `/settings/teams/${p.teamDomain}/members`,
  "up.members.invites": (p: { teamDomain: string }) => `/settings/teams/${p.teamDomain}/members/invites`,
  "up.notifications.confirmation": "/email_notification",
  "up.onboarding": (p: { teamDomain: string }) => `/welcome/${p.teamDomain}`,
  "up.onboarding.purchase": (p: { teamDomain: string }) => `/welcome/${p.teamDomain}/purchase`,
  "up.quotes.confirmation.show": (p: { quotePublicKey: string }) => `/quote/${p.quotePublicKey}/confirmation`,
  "up.quotes.show": (p: { orderForm?: boolean; quotePublicKey: string }) =>
    `/quote/${p.quotePublicKey}${query({ orderForm: p.orderForm })}`,
  "up.referrals": "/settings/referrals",
  "up.root": (p: { utmParams?: UtmParams }) => `/${query({ ...p.utmParams })}`,
  "up.settings.authTokens.new": "/auth/tokens/new",
  "up.settings.billing": (p: { teamDomain: string }) => `/settings/teams/${p.teamDomain}/billing`,
  "up.settings.billing.history": (p: { teamDomain: string }) => `/settings/teams/${p.teamDomain}/billing/history`,
  "up.settings.billing.subaccounts": (p: { teamDomain: string }) =>
    `/settings/teams/${p.teamDomain}/billing/subaccounts`,
  "up.settings.billing.upgrade": (p: { feature: Feature; teamDomain: string }) =>
    `/settings/teams/${p.teamDomain}/billing/upgrade${query({ feature: p.feature })}`,
  "up.settings.general": (p: { section?: string; teamDomain: string }) =>
    `/settings/teams/${p.teamDomain}${p.section ? `/${p.section}` : ""}`,
  "up.settings.manageTeams": (_p: Record<string, never>) => "/manage_teams",
  "up.settings.personal": "/settings",
  "up.settings.team": (p: { teamDomain: string }) => `/settings/teams/${p.teamDomain}/members`,
  "up.shortCuts.purchase": "/purchase",
  "web.legal": "/legal",
  "web.security": "/security",
} as const;

export const serverResponsePaths = {
  "hs.extensionRedirect": (p: { utmParams?: UtmParams }) => `/extension${query({ ...p.utmParams })}`,
  "up.auth.loginRedirect": (p: { redirectUrl?: Url }) =>
    "/api/auth/crossDomain/redirect" +
    query({
      redirectUrl: p.redirectUrl ?? location.href,
    }),
  "up.auth.oauth.redirect": (p: {
    provider: "google";
    redirectUrl?: string;
    returnHere?: boolean;
    subdomain?: string;
  }) =>
    `/auth/oauth/${p.provider}${query({ redirectUrl: p.redirectUrl ?? (p.returnHere ? location.href : null), subdomain: p.subdomain })}`,
  "up.auth.saml.redirect": (p: { redirectUrl?: string; returnHere?: boolean; teamUniqueIdentifier: string }) =>
    `/auth/saml/${p.teamUniqueIdentifier}/authenticate${query({ redirectUrl: p.redirectUrl ?? (p.returnHere ? location.href : null) })}`,
} as const;

const allPaths = {
  ...paths,
  ...serverResponsePaths,
};

export type DynamicPaths = {
  [key in keyof typeof allPaths]: (typeof allPaths)[key] extends Function ? key : never;
}[keyof typeof allPaths];

export type PathParameters =
  | {
      [key in DynamicPaths]: [key, ...Parameters<(typeof allPaths)[key]>];
    }[DynamicPaths]
  | {
      [key in StaticPaths]: [key];
    }[StaticPaths];

export type StaticPaths = {
  [key in keyof typeof allPaths]: (typeof allPaths)[key] extends string ? key : never;
}[keyof typeof allPaths];

export function cleanRedirectPath(url: string): DirectLink {
  if (url.startsWith("/")) return (getRoot("up").slice(0, -1) + url) as Url;

  if (url.startsWith(getRoot("up")) || url.startsWith(getRoot("hs")) || url.startsWith(getRoot("hq")))
    return path("up.auth.loginRedirect", { redirectUrl: url });

  return getRoot("up");
}

export function getRoot(service: "hq" | "hs" | "up"): Url {
  return (getRouteOrigin(`${service}.root`) + "/") as Url;
}

export function getRouteBaseHostname(routeKey: keyof typeof allPaths): string {
  const tld = location.hostname.split(".").pop()!;

  if (routeKey.startsWith("web.")) return `helloscreen.${tld}`;

  if (routeKey.startsWith("up.")) return `account.helloscreen.${tld}`;

  if (routeKey.startsWith("hq.")) return `hq.helloscreen.${tld}`;

  if (routeKey.startsWith("hs.")) return `helloscreen.${tld}`;

  throw new Error("Unknown path prefix");
}

export function getRouteOrigin(routeKey: keyof typeof allPaths): Url {
  const protocol = location.protocol as "http:" | "https:";
  return `${protocol}//${getRouteBaseHostname(routeKey)}`;
}

export function isTrustedOrigin(origin: string): boolean {
  const originRoot = origin + "/";

  return originRoot === getRoot("up") || originRoot === getRoot("hs") || originRoot === getRoot("hq");
}

export function path(...pathData: PathParameters): DirectLink {
  const [pathName, ...args] = pathData;
  let path;
  path =
    typeof allPaths[pathName] === "string"
      ? allPaths[pathName as StaticPaths]
      : (allPaths[pathName as DynamicPaths] as (args: any) => string).apply(null, args as any);

  const origin = getRouteOrigin(pathName);

  if (origin !== location.origin) {
    path = `${origin}${path}`;
    if (new URL(path).pathname === "/") path += path.includes("?") ? "&authenticated=true" : "?authenticated=true";
  }

  return path as DirectLink;
}

function query(object: Record<string, any>) {
  const params = Object.entries(object).filter(([key, value]) => value !== null && value !== undefined);

  if (params.length === 0) return "";

  return "?" + params.map(([key, value]) => `${decamelize(key)}=${encodeURIComponent(value)}`).join("&");
}
